import { useEffect, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { fetchUserProfile } from "../Api";
import { STORAGE_KEYS } from "../constants";
import type { ProfileData, ShopItem, VipStatus } from "../types";
import { getStorageItem } from "../utils";

export function useGiftFlow() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [targetUser, setTargetUser] = useState<ProfileData | null>(null);
  const [highlight, setHighlight] = useState(false);
  const [targetProduct, setTargetProduct] = useState<ShopItem | null>(null);
  const [vipStatus, setVipStatus] = useState<VipStatus | null>(null);
  const [isAutoFetching, setIsAutoFetching] = useState(false);
  const [cameFromDirectLink, setCameFromDirectLink] = useState(false);
  const [prefillPlayerId, setPrefillPlayerId] = useState("");

  useEffect(() => {
    const stepParam = searchParams.get("step");
    if (!stepParam) return;

    const step = parseInt(stepParam, 10);
    if (Number.isNaN(step)) return;

    if (step === 0) {
      setCurrentStep(0);
    } else if (step >= 1 && targetUser) {
      setCurrentStep(1);
      if (step !== 1) {
        setSearchParams({ playerId: targetUser.id.toString(), step: "1" });
      }
    }
  }, [location.hash, searchParams, setSearchParams, targetUser]);

  useEffect(() => {
    const playerIdParam = searchParams.get("playerId");
    if (!playerIdParam) return;
    if (targetUser || currentStep > 0) return;

    const playerId = Number(playerIdParam);
    if (!Number.isInteger(playerId) || playerId <= 0) return;

    const autoFetchPlayer = async () => {
      setIsAutoFetching(true);
      try {
        const response = await fetchUserProfile(playerId);
        if (response.success && response.data) {
          setTargetUser(response.data);
          setCurrentStep(1);
          setCameFromDirectLink(true);
        }
      } catch (error) {
        console.error("Failed to auto-fetch player:", error);
      } finally {
        setIsAutoFetching(false);
      }
    };

    autoFetchPlayer();
  }, [searchParams, targetUser, currentStep]);

  useEffect(() => {
    const playerIdParam = searchParams.get("playerId");
    if (playerIdParam) return;
    if (targetUser || currentStep > 0 || prefillPlayerId) return;

    const savedPlayerId = getStorageItem(STORAGE_KEYS.lastPlayerId);
    if (!savedPlayerId) return;

    const numericId = Number(savedPlayerId);
    if (!Number.isInteger(numericId) || numericId <= 0) return;

    setPrefillPlayerId(savedPlayerId);
  }, [searchParams, targetUser, currentStep, prefillPlayerId]);

  function handlePlayerChange(profile: ProfileData | null) {
    setTargetUser(profile);
    if (profile) {
      setSearchParams({ playerId: profile.id.toString(), step: "1" });
      setCurrentStep(1);
    } else {
      setSearchParams({});
      setCurrentStep(0);
    }
  }

  function resetPlayer() {
    setTargetUser(null);
    setTargetProduct(null);
    setVipStatus(null);
    setCurrentStep(0);
    setSearchParams({});
    setCameFromDirectLink(false);
  }

  function selectProduct(product: ShopItem, vipStatusData: VipStatus | null) {
    setTargetProduct(product);
    setVipStatus(vipStatusData);
  }

  function markDirectLinkHandled() {
    setCameFromDirectLink(false);
  }

  return {
    currentStep,
    setCurrentStep,
    targetUser,
    targetProduct,
    vipStatus,
    prefillPlayerId,
    highlight,
    setHighlight,
    isAutoFetching,
    cameFromDirectLink,
    handlePlayerChange,
    resetPlayer,
    selectProduct,
    markDirectLinkHandled,
  };
}
